import * as React from 'react';
import {
  DefaultButton,
  Callout,
  DirectionalHint,
  Stack,
  Text,
  IconButton,
  SearchBox,
  FocusZone,
  FocusZoneDirection,
  Separator,
  mergeStyleSets,
  getTheme,
  Label
} from '@fluentui/react';

/**
 * MultiPickerFluent (Fluent UI v8)
 * A Fluent-style multi-select "Dropdown" that opens a Callout with two panes:
 *  - Left: Selected items (with ✕ remove)
 *  - Right: Available items (with + add)
 *
 * Props:
 * - selected: Array<string | number>  // ids of selected items
 * - options: Array<{ id: string | number; label?: string; [k: string]: any }>
 * - onAdd: (item) => void
 * - onRemove: (item) => void
 * - onRenderItem?: (item, ctx: { selected: boolean }) => React.ReactNode
 * - getLabel?: (item) => string
 * - placeholder?: string
 * - buttonTextMax?: number // max chips/labels to show on the button before "+N"
 * - className?: string
 */
export default function MultiPickerFluent({
  selected = [],
  options = [],
  onAdd,
  onRemove,
  onRenderItem,
  getLabel,
  searchKey = 'name',
  placeholder = 'Select…',
  buttonTextMax = 3,
  className = '',
  label=""
}) {

  const theme = getTheme();
  const styles = React.useMemo(() =>
    mergeStyleSets({
      button: {
        minWidth: 260,
        maxWidth: 260,
        display: 'flex',
        justifyContent: 'space-between',
      },
      callout: { width: 600, padding: 12, background: '#fff' },
      header: { padding: '4px 8px' },
      column: {
        border: `1px solid ${theme.palette.neutralLight}`,
        borderRadius: 6,
        background: theme.palette.white,
        overflow: 'hidden',
        width: '50%'
      },
      list: { maxHeight: 280, overflowY: 'auto', padding: 8 },
      row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: theme.palette.white,
        padding: '6px 8px',
        borderRadius: 4,
        boxShadow: theme.effects.elevation4,
        marginBottom: 6,
      },
      chip: {
        selectors: {
          '&': {
            border: `1px solid ${theme.palette.neutralLight}`,
            borderRadius: 999,
            padding: '2px 8px',
            fontSize: 12,
            marginRight: 4,
            background: theme.palette.white,
          },
        },
      },
      footer: { marginTop: 8 },
    }),
  [theme]);

  const btnRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const labelOf = React.useCallback(
    (item) => (getLabel ? getLabel(item) : item[searchKey] ?? String(item.id)),
    [getLabel, searchKey]
  );

  const selectedSet = React.useMemo(() => new Set(selected), [selected]);
  const selectedItems = React.useMemo(
    () => options.filter((o) => selectedSet.has(o.id)),
    [options, selectedSet]
  );
  const availableItems = React.useMemo(
    () => options.filter((o) => !selectedSet.has(o.id)),
    [options, selectedSet]
  );

  const filter = (arr) =>
    query
      ? arr.filter((i) => labelOf(i).toLowerCase().includes(query.toLowerCase()))
      : arr;

  const filteredSelected = React.useMemo(() => filter(selectedItems), [selectedItems, query]);
  const filteredAvailable = React.useMemo(() => filter(availableItems), [availableItems, query]);

  React.useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    if (open) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  // Button caption: show up to N labels, then +N more
  const caption = React.useMemo(() => {
    if (selectedItems.length === 0) return placeholder;
    const labels = selectedItems.map(labelOf);
    const shown = labels.slice(0, buttonTextMax).join(', ');
    const more = labels.length - buttonTextMax;
    return more > 0 ? `${shown} +${more} more` : shown;
  }, [selectedItems, labelOf, placeholder, buttonTextMax]);

  return (
    <>
      <Label>
        {label}
      </Label>
      <div ref={btnRef}>
        <DefaultButton
          text={caption}
          styles={{
            root: {width: '100%', display: 'flex', justifyContent: "space-between"}, 
            flexContainer: {display: 'flex', justifyContent: 'space-between', width: '100%'},
            textContainer: {textAlign: 'left', flex: 1, maxWidth: '90%'}
          }}
          onRenderText={(text) => <Text variant="medium" block nowrap>{text.text}</Text>}
          onClick={() => setOpen((o) => !o)}
          menuIconProps={{ iconName: open ? 'ChevronUp' : 'ChevronDown' }}
          // styles={{ root: styles.button }}
        />
      </div>

      {open && btnRef.current && (
        <Callout
          target={btnRef.current}
          onDismiss={() => setOpen(false)}
          setInitialFocus
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible={false}
          className={styles.callout}
        >
          <Stack tokens={{ childrenGap: 8 }}>
            <SearchBox
              placeholder="Search…"
              value={query}
              onChange={(_, v) => setQuery(v || '')}
              underlined
            />

            <Stack horizontal tokens={{ childrenGap: 12 }}>
              {/* Selected */}
              <Stack grow className={styles.column}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={styles.header}>
                  <Text variant="medium">Selected</Text>
                  <Text variant="xSmall">{filteredSelected.length}</Text>
                </Stack>
                <Separator styles={{ root: { margin: 0 } }} />
                <FocusZone direction={FocusZoneDirection.vertical}>
                  <div className={styles.list}>
                    {filteredSelected.length === 0 ? (
                      <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>No items</Text>
                    ) : (
                      filteredSelected.map((item) => (
                        <div key={item.id} className={styles.row}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            {onRenderItem ? onRenderItem(item, { selected: true }) : (
                              <Text variant="small" block nowrap>{labelOf(item)}</Text>
                            )}
                          </div>
                          <IconButton
                            iconProps={{ iconName: 'Cancel' }}
                            title="Remove"
                            ariaLabel={`Remove ${labelOf(item)}`}
                            onClick={() => onRemove?.(item)}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </FocusZone>
              </Stack>

              {/* Available */}
              <Stack grow className={styles.column}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={styles.header}>
                  <Text variant="medium">Available</Text>
                  <Text variant="xSmall">{filteredAvailable.length}</Text>
                </Stack>
                <Separator styles={{ root: { margin: 0 } }} />
                <FocusZone direction={FocusZoneDirection.vertical}>
                  <div className={styles.list}>
                    {filteredAvailable.length === 0 ? (
                      <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>No items</Text>
                    ) : (
                      filteredAvailable.map((item) => (
                        <div key={item.id} className={styles.row}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            {onRenderItem ? onRenderItem(item, { selected: false }) : (
                              <Text variant="small" block nowrap>{labelOf(item)}</Text>
                            )}
                          </div>
                          <IconButton
                            iconProps={{ iconName: 'Add' }}
                            title="Add"
                            ariaLabel={`Add ${labelOf(item)}`}
                            onClick={() => onAdd?.(item)}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </FocusZone>
              </Stack>
            </Stack>

          </Stack>
        </Callout>
      )}
    </>
  );
}

/**
 * Example usage:
 *
 * const [selected, setSelected] = React.useState([1, 3]);
 * const options = [
 *   { id: 1, label: 'Alpha' },
 *   { id: 2, label: 'Bravo' },
 *   { id: 3, label: 'Charlie' },
 *   { id: 4, label: 'Delta' },
 * ];
 *
 * <MultiPickerFluent
 *   selected={selected}
 *   options={options}
 *   onAdd={(item) => setSelected((s) => [...s, item.id])}
 *   onRemove={(item) => setSelected((s) => s.filter((id) => id !== item.id))}
 *   onRenderItem={(item, { selected }) => (
 *     <Stack horizontal tokens={{ childrenGap: 6 }}>
 *       <Text>{item.label}</Text>
 *       {!selected && item.meta && (
 *         <Text variant="xSmall" styles={{ root: { color: getTheme().palette.neutralSecondary }}}>
 *           {item.meta}
 *         </Text>
 *       )}
 *     </Stack>
 *   )}
 * />
 */
